import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SharedLanguageService } from '@language';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { QueryPartStrategy } from '../../advanced-search/filters/base/entities/query-part-strategy';
import { QueryPart } from '../../advanced-search/filters/base/entities/query-part.class';
import { ConfigService } from '../../config/data-services/config.abstract.data-service';
import { ApiQuery, Combine, Restriction } from '../../core/api/entities/api-query.entity';
import { DottedKey } from '../../core/field-definitions/entities/field-definition.entity';
import { FacetProfileTag, ProfileDataService } from '../../core/profiles/data-services/profile.data-service';
import { FacetProfile } from '../../core/profiles/entities/profile.entity';
import { ProfileHelper } from '../../core/profiles/helpers/profile.helper';
import { UrlQueryParams } from '../../core/query/entities/url-query-params.entity';
import { ShUrlQueryService } from '../../core/query/services/url-query.service';
import { ApiFragment } from '../../core/records/entities/api-fragment.class';
import { Metadata } from '../../core/records/entities/metadata/metadata.entity';
import { ShRecordService } from '../../core/records/services/record.service';
import { UserService } from '../../core/users/services/user.service';
import { FormSelectOption } from '../../form-controls/entities/form-select-option.entity';
import { isNotNullOrUndefined } from '../../helpers/typed-reactive-filters.helper';
import { SortInfo } from '../../overviews/generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { SortOption } from '../../overviews/generic-overview/components/display-settings-bar/entities/sort-option.entity';
import { PaginatedResults } from '../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { RecordColumn } from '../../overviews/list-view/entities/record-column.entity';
import { ListProfileColumnService } from '../../overviews/list-view/services/list-profile-column.service';
import { SearchState } from '../../overviews/search-page/entities/search-page-state.entity';
import { QueryIterator } from '../entities/query-iterator.entity';
import { QueryStateHandler } from '../services/query-state-handler.service';

@Injectable()
export class RecordSearchPageSandbox {
    protected readonly queryStateHandler: QueryStateHandler = inject(QueryStateHandler);
    protected readonly urlQueryService: ShUrlQueryService = inject(ShUrlQueryService);
    protected readonly searchService: ShRecordService = inject(ShRecordService);

    private readonly configService: ConfigService<any> = inject(ConfigService);

    private readonly usersService: UserService = inject(UserService);

    private readonly profileService: ProfileDataService = inject(ProfileDataService);
    private readonly profileHelper: ProfileHelper = inject(ProfileHelper);

    private readonly columnService: ListProfileColumnService = inject(ListProfileColumnService);
    private readonly translateService: SharedLanguageService = inject(SharedLanguageService);

    // #region Query
    public getQueryParams(): UrlQueryParams {
        return this.urlQueryService.getUrlQueryParams();
    }

    public createQueryContext(args: Pick<QueryIterator, 'sortInfo' | 'query' | 'resultsPerPage'>): QueryIterator {
        return this.queryStateHandler.create(args);
    }

    public setActive(queryIteratorId: string, fragment: ApiFragment): void {
        this.queryStateHandler.setActive(queryIteratorId, fragment.getFragmentId());
    }

    public getQueryContextPage(
        iterator: string,
        pageNumber: number,
        setLoading: boolean = true
    ): Observable<PaginatedResults<ApiFragment>> {
        return this.queryStateHandler.getPage(iterator, pageNumber, setLoading);
    }
    // #endregion

    // #region Search
    public getAllRecordsWithId(
        fragmentIds: string[],
        searchState: SearchState,
        restriction: Restriction,
        fieldSelect: DottedKey[]
    ): Observable<ApiFragment[]> {
        if (fragmentIds.length === 0) {
            return of([]);
        }

        const fragmentQuery: QueryPart = new QueryPart({
            field: 'FragmentId',
            strategy: QueryPartStrategy.MatchAtLeastOne,
            values: fragmentIds
        });
        const restrictionParts = [restriction, Combine.And(...[fragmentQuery])];

        const fragmentsRestriction: Restriction = Combine.And(...restrictionParts);

        const query: ApiQuery = new ApiQuery(searchState.structuredQuery, fragmentsRestriction, fieldSelect);

        return this.searchService.getAllRecords(query).pipe(
            map(results => results.map(record => new ApiFragment(record.metadata as Metadata))),
            catchError(err => this.handleRestrictionError(err))
        );
    }

    protected handleRestrictionError(error: Error): Observable<ApiFragment[]> {
        console.error(error);

        return of([]);
    }
    // #endregion

    // #region Facets
    public getFacetProfile(facetProfileTag: FacetProfileTag): Observable<FacetProfile> {
        return this.usersService.getCurrentUser().pipe(
            map(user => user.Organisation.Id),
            switchMap(orgId => this.profileService.getFacetProfiles(orgId, facetProfileTag)),
            switchMap(profiles => this.profileHelper.filterByLanguage(profiles)),
            map(profiles => this.profileHelper.mergeProfiles(profiles)),
            isNotNullOrUndefined()
        );
    }
    // #endregion

    // #region Columns
    public getColumnOptions(profileTag: string): Observable<FormSelectOption[]> {
        return this.columnService.getColumnOptions(profileTag);
    }

    public getSortOptions(profileTag: string, defaultSortInfo: SortInfo[]): Observable<SortOption[]> {
        return this.columnService.getSortOptions(profileTag, defaultSortInfo);
    }

    public getAllProfileColumns(profileTag: string): Observable<RecordColumn[]> {
        return this.columnService.getAllProfileColumns(profileTag);
    }

    public getColumns(searchPageId: string, profileTag: string): Observable<RecordColumn[]> {
        return this.columnService.getVisibleProfileColumns(profileTag, searchPageId);
    }

    public setColumns(searchPageId: string, columnKeys: string[]): void {
        return this.columnService.setVisibleProfileColumns(searchPageId, columnKeys);
    }
    // #endregion

    // #region Features
    public get $pimPluginEnabled(): Signal<boolean> {
        return toSignal(this.configService.get('enablePimFeatures'));
    }
    // #endregion

    // #region Utils
    public translate(token: string, params?: Object | undefined): string {
        return this.translateService.instant(token, params);
    }
    // #endregion
}
